import { useState, type ReactNode } from 'react';
import { T, useGT, Var } from 'gt-react';
import {
  ArrowLeftIcon,
  CaretDoubleLeftIcon,
  CaretDoubleRightIcon,
  SparkleIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { REPORT_CANVAS_COLUMNS } from '@erp/shared-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ReportPalette } from './ReportPalette';
import { useComponentTitle } from './ReportCanvas';
import { useComponentDefinition } from './registry-context';
import type { ComponentGenerationControls } from './useCanvasGeneration';
import type { ReportComponentConfig } from './types';
import { IconTooltip } from '@/components/common/IconTooltip';

/** The component the sidebar edits, with its size on the grid. */
export interface SidebarSelection {
  component: ReportComponentConfig;
  w: number;
  h: number;
  /** The widest it can be from its column. */
  maxW: number;
  /** The smallest its type may be. */
  minW: number;
  minH: number;
}

/** Heights the Height select offers at least up to, in grid rows. */
const LISTED_HEIGHTS = 24;

function range(from: number, to: number): number[] {
  return Array.from({ length: Math.max(0, to - from + 1) }, (_, i) => from + i);
}

/** One size select: the component's width in columns, or height in rows. */
function SizeSelect({
  id,
  label,
  value,
  options,
  optionLabel,
  onChange,
}: {
  id: string;
  label: string;
  value: number;
  options: number[];
  optionLabel: (value: number) => ReactNode;
  onChange: (value: number) => void;
}) {
  return (
    <div className="min-w-0 flex-1 space-y-1.5">
      <Label htmlFor={id} className="text-xs">
        {label}
      </Label>
      <Select
        value={String(value)}
        onValueChange={(next) => onChange(Number(next))}
      >
        <SelectTrigger id={id} className="h-8 text-xs" aria-label={label}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option} value={String(option)}>
              {optionLabel(option)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/** Room left for the machine-facing wrapper within the contract's 2,000 characters. */
const MAX_COMPONENT_PROMPT = 1500;

/** "Edit with AI" for the selected component only. */
function ComponentPrompt({
  component,
  generation,
  onUpdate,
}: {
  component: ReportComponentConfig;
  generation: ComponentGenerationControls;
  onUpdate: (component: ReportComponentConfig) => void;
}) {
  const gt = useGT();
  const [prompt, setPrompt] = useState('');
  const run = async () => {
    const generated = await generation.generate(component, prompt);
    if (!generated) return;
    onUpdate(generated);
    setPrompt('');
  };
  return (
    <div className="space-y-1.5">
      <Label htmlFor={`component-prompt-${component.id}`} className="text-xs">
        <T context="report canvas sidebar component AI prompt label">
          Edit with AI
        </T>
      </Label>
      <Textarea
        id={`component-prompt-${component.id}`}
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void run();
        }}
        maxLength={MAX_COMPONENT_PROMPT}
        rows={3}
        disabled={generation.isGenerating}
        placeholder={gt(
          'Describe this chart — e.g. "top 10 customers by net, as bars"',
          { context: 'report canvas sidebar component AI prompt placeholder' },
        )}
        className="text-xs"
      />
      <Button
        size="sm"
        className="h-7 w-full gap-1 text-xs"
        disabled={generation.isGenerating || !prompt.trim()}
        onClick={() => void run()}
      >
        <SparkleIcon className="h-4 w-4" />
        {generation.isGenerating
          ? gt('Generating…', { context: 'report canvas sidebar generating' })
          : gt('Generate', { context: 'report canvas sidebar generate' })}
      </Button>
    </div>
  );
}

/** The selected component's settings: title, size, its config form and AI prompt. */
function ComponentPanel({
  selection,
  onUpdate,
  onDelete,
  onSizeChange,
  generation,
}: {
  selection: SidebarSelection;
  onUpdate: (component: ReportComponentConfig) => void;
  onDelete: () => void;
  onSizeChange: (size: { w: number; h: number }) => void;
  generation?: ComponentGenerationControls;
}) {
  const gt = useGT();
  const { component, w, h, maxW, minW, minH } = selection;
  const definition = useComponentDefinition(component.type);
  const { fallback } = useComponentTitle(component);
  const ConfigForm = definition?.ConfigForm ?? null;
  return (
    <div className="space-y-4 p-3">
      <div className="space-y-1.5">
        <Label htmlFor={`component-title-${component.id}`} className="text-xs">
          <T context="report canvas sidebar component title">Title</T>
        </Label>
        <Input
          id={`component-title-${component.id}`}
          value={component.title ?? ''}
          maxLength={200}
          placeholder={fallback ?? undefined}
          onChange={(e) => onUpdate({ ...component, title: e.target.value })}
          className="h-8 text-xs"
        />
      </div>
      <div className="flex gap-2">
        <SizeSelect
          id={`component-width-${component.id}`}
          label={gt('Width', {
            context: 'report canvas sidebar component width',
          })}
          value={w}
          options={range(Math.min(minW, maxW), maxW)}
          optionLabel={(columns) => (
            <T context="report canvas component width option">
              <Var>{columns}</Var> of <Var>{REPORT_CANVAS_COLUMNS}</Var> columns
            </T>
          )}
          onChange={(next) => onSizeChange({ w: next, h })}
        />
        <SizeSelect
          id={`component-height-${component.id}`}
          label={gt('Height', {
            context: 'report canvas sidebar component height',
          })}
          value={h}
          options={range(minH, Math.max(LISTED_HEIGHTS, h))}
          optionLabel={(rows) => (
            <T context="report canvas component height option">
              <Var>{rows}</Var> rows
            </T>
          )}
          onChange={(next) => onSizeChange({ w, h: next })}
        />
      </div>
      {generation && !definition?.generatesInConfigForm && (
        <ComponentPrompt
          key={component.id}
          component={component}
          generation={generation}
          onUpdate={onUpdate}
        />
      )}
      {ConfigForm && <ConfigForm component={component} onUpdate={onUpdate} />}
      <Button
        variant="outline"
        size="sm"
        className="h-7 w-full gap-1 text-xs text-destructive"
        onClick={onDelete}
      >
        <TrashIcon className="h-4 w-4" />
        <T context="report canvas sidebar delete component">Delete component</T>
      </Button>
    </div>
  );
}

/**
 * The canvas's right sidebar: the component palette, or the selected
 * component's edit panel. Collapses to a narrow gutter. Must render inside
 * the canvas's registry, data and drag-and-drop contexts.
 */
export function ReportCanvasSidebar({
  collapsed,
  onCollapsedChange,
  selection,
  onDeselect,
  onUpdate,
  onDelete,
  onSizeChange,
  onAdd,
  generation,
  reportSettings,
}: {
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  selection: SidebarSelection | null;
  onDeselect: () => void;
  onUpdate: (component: ReportComponentConfig) => void;
  onDelete: () => void;
  onSizeChange: (size: { w: number; h: number }) => void;
  /** Adds a component in the first free spot at the bottom of the grid. */
  onAdd: (componentType: string) => void;
  generation?: ComponentGenerationControls;
  /** The whole Report's settings (its colors, …), shown above the components while none is selected. */
  reportSettings?: ReactNode;
}) {
  const gt = useGT();
  const definition = useComponentDefinition(selection?.component.type ?? '');
  if (collapsed)
    return (
      <aside className="flex w-9 shrink-0 flex-col items-center border-l py-2">
        <IconTooltip>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-label={gt('Show the components panel', {
              context: 'report canvas sidebar expand',
            })}
            aria-expanded={false}
            onClick={() => onCollapsedChange(false)}
          >
            <CaretDoubleLeftIcon className="h-4 w-4" />
          </Button>
        </IconTooltip>
      </aside>
    );
  return (
    <aside
      className="flex w-72 shrink-0 flex-col border-l"
      aria-label={gt('Components panel', {
        context: 'report canvas sidebar',
      })}
    >
      <div className="flex h-9 shrink-0 items-center gap-1 border-b px-2">
        {selection ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 min-w-0 gap-1 px-1.5 text-xs"
            onClick={onDeselect}
          >
            <ArrowLeftIcon className="h-4 w-4 shrink-0" />
            <span className="truncate">
              {definition?.label ?? selection.component.type}
            </span>
          </Button>
        ) : (
          <p className="px-1.5 text-xs font-medium">
            <T context="report canvas sidebar title">Report builder</T>
          </p>
        )}
        <IconTooltip>
          <Button
            variant="ghost"
            size="icon"
            className="ml-auto h-7 w-7 shrink-0"
            aria-label={gt('Hide the components panel', {
              context: 'report canvas sidebar collapse',
            })}
            aria-expanded
            onClick={() => onCollapsedChange(true)}
          >
            <CaretDoubleRightIcon className="h-4 w-4" />
          </Button>
        </IconTooltip>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {selection ? (
          <ComponentPanel
            selection={selection}
            onUpdate={onUpdate}
            onDelete={onDelete}
            onSizeChange={onSizeChange}
            generation={generation}
          />
        ) : (
          <>
            {reportSettings && (
              <div className="space-y-3 border-b p-3">{reportSettings}</div>
            )}
            <ReportPalette onAdd={onAdd} compact />
          </>
        )}
      </div>
    </aside>
  );
}
